import type { SerializedStyles } from "@emotion/react";
import { type ReactNode, type RefObject, useRef } from "react";
import {
  type AriaButtonOptions,
  mergeProps,
  useButton,
  useHover,
} from "react-aria";

import { createStaticStyles } from "../css";

export type BaseButtonProps = {
  triggerRef?: RefObject<HTMLButtonElement>;
  triggerProps?: AriaButtonOptions<"button">;
  children?: ReactNode;
  disabled?: boolean;
  active?: boolean;
  onPress?: () => void;
  type?: "button" | "submit" | "reset";
  id?: string;
  styles?: SerializedStyles;
  waterfall?: (state: {
    isDisabled?: boolean;
    isHovered?: boolean;
    isPressed?: boolean;
  }) => SerializedStyles;
};

const baseButtonStyles = createStaticStyles.clean({
  outline: "none",
  display: "flex",
  alignItems: "center",
  columnGap: "8px",
  justifyContent: "center",
  transition: "all 0.2s ease",
  whiteSpace: "nowrap",
  width: "auto",
  "&:hover": {
    cursor: "pointer",
  },
});

export const BaseButton = ({
  children,
  disabled,
  id,
  onPress,
  waterfall,
  triggerProps,
  triggerRef: outerRef,
  type,
  active,
  styles,
}: BaseButtonProps) => {
  const innerRef = useRef<HTMLButtonElement>(null);
  const ref = outerRef || innerRef;

  const { hoverProps, isHovered } = useHover({});
  const { buttonProps, isPressed } = useButton(
    {
      ...triggerProps,
      onPress: (e) => {
        onPress?.();
        triggerProps?.onPress?.(e);
      },
      isDisabled: disabled,
      type: type,
    },
    ref as RefObject<HTMLButtonElement>,
  );

  return (
    <button
      ref={ref}
      css={[
        baseButtonStyles,
        {
          "&:hover": {
            cursor: disabled ? "initial" : "pointer",
          },
        },
        waterfall?.({
          isHovered,
          isPressed: active || isPressed,
          isDisabled: disabled,
        }),
        styles,
      ]}
      id={id}
      {...mergeProps(buttonProps, hoverProps)}
    >
      {children}
    </button>
  );
};
